/**
 * MachineScheduleNonWorkOverlay.tsx
 * A "closed" veil drawn IN FRONT of the booking bars on every non-work day
 * (weekend or stat holiday), so a multi-day bar that spans a closed day is
 * visibly faded there — you can see the lab is shut even though the bar runs
 * across it, and the working part stays full-strength.
 *
 * Why a SOLID veil, not a hatch (2026-06-20): the previous diagonal hatch read as
 * the SAME pattern the tentative bars already use (45° sage stripes), so the two
 * blended and the weekend was hard to pick out. A solid translucent veil — neutral
 * grey for weekends, amber (the app's "closed/maintenance" hue) for holidays — is
 * categorically different from the bar hatch and clearly says "no work here."
 *
 * Run Mode Phase C (G-3 / BUG-2026-0141): a CONTINUOUS (24/7) booking really
 * runs through weekends, so veiling its covered closed days as "lab closed"
 * would mislabel legitimate run-time. Days in `continuousDays` swap to a faint
 * continuous-token tint + a "running (24/7)" tooltip — visually distinct from
 * both a working day and a closed day (the lab is still closed for PEOPLE; the
 * machine keeps running). Attended rows keep the full closed veil.
 *
 * Pure presentational: positions match the bars (fractions of the day window) and
 * all colours are theme tokens resolved by the parent. pointer-events:none so drags
 * still reach the bars underneath; zIndex sits above bars (1) but below the
 * create-drag rectangle (5) and the jump-to flash (6).
 */

import React from 'react';
import { Box } from '@mui/material';
import { isWeekend } from './scheduleGridShared';

export interface MachineScheduleNonWorkOverlayProps {
  /** ISO dates ('YYYY-MM-DD') for the visible window, in order. */
  days: string[];
  /** ISO date → stat-holiday name (for the per-band tooltip). */
  holidayNameByDate: Map<string, string>;
  /** Resolved solid-veil colours (already alpha-adjusted) from the parent. */
  colors: { holiday: string; weekend: string; continuousRun: string };
  /**
   * ISO days covered by a CONTINUOUS (24/7) booking on THIS machine row —
   * the veil renders "running" there instead of "lab closed". Omit/empty ⇒
   * every non-work day gets the classic closed veil (attended-only rows).
   */
  continuousDays?: ReadonlySet<string>;
}

export const MachineScheduleNonWorkOverlay: React.FC<MachineScheduleNonWorkOverlayProps> = ({
  days,
  holidayNameByDate,
  colors,
  continuousDays,
}) => {
  const totalDays = days.length || 1;
  return (
    <>
      {days.map((day, i) => {
        const holidayName = holidayNameByDate.get(day);
        const weekend = isWeekend(day);
        if (!holidayName && !weekend) return null;
        const running = continuousDays?.has(day) ?? false;
        const veil = running
          ? colors.continuousRun
          : holidayName
            ? colors.holiday
            : colors.weekend;
        const title = running
          ? `${holidayName ?? 'Weekend'} — continuous test running (24/7)`
          : holidayName
            ? `${holidayName} (lab closed)`
            : 'Weekend (lab closed)';
        return (
          <Box
            key={`nonwork-${day}`}
            title={title}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(i / totalDays) * 100}%`,
              width: `${(1 / totalDays) * 100}%`,
              pointerEvents: 'none',
              // Above the bars (2) so closed days visibly notch them, but BELOW
              // the sticky machine-label cells (4) — at 4 the translucent bands
              // washed across the frozen labels on horizontal scroll and made
              // them look transparent (2026-07-24 round 4).
              zIndex: 3,
              backgroundColor: veil,
              // Faint inner edges (in the band's own colour) so a run of closed
              // days reads as a distinct "closed" block, not a darker bar segment.
              boxShadow: `inset 1px 0 0 ${veil}, inset -1px 0 0 ${veil}`,
            }}
          />
        );
      })}
    </>
  );
};
